import { isAdminPath } from '../admin/sections'
import type { AdminUser } from '../admin/api'
import type { Client } from '../data/clients'
import type { Role } from '../lib/roles'
import type { GuestSession, Membership, SessionUser } from './api'

/**
 * What the shell says after a transition the boundary made: a company joined from the
 * Invitations page, or the active company lost.
 */
export type Notice =
  | { kind: 'joined'; companyId: string; name: string; role: Role }
  | { kind: 'lost'; lostName: string; nextName: string }

/** The invite pages that end the flow; the stored token is cleared once one renders. */
export type InviteTerminal = 'expired' | 'revoked' | 'used' | 'member'

export type BoundaryState =
  | { status: 'checking' }
  | { status: 'unauthenticated'; expired?: boolean }
  | { status: 'request' }
  | { status: 'unavailable' }
  | { status: 'wizard'; user: SessionUser; memberships: Membership[] }
  | { status: 'authenticated'; user: SessionUser; memberships: Membership[]; clients: Client[]; notice: Notice | null }
  | {
    status: 'noAccess'
    user: SessionUser
    memberships: Membership[]
    /** The company whose loss in this session led here, for the removed statement; `null` otherwise. */
    removed: string | null
  }
  | {
    status: 'invite'
    /** `null` while signed out: the link page needs no session. */
    user: SessionUser | null
    memberships: Membership[]
    clients: Client[]
    /** Set once a terminal page rendered; it keeps the page without reading the token. */
    terminal: InviteTerminal | null
  }
  | { status: 'admin'; user: SessionUser; users: AdminUser[] }
  /** `/share`: the page opens the link in its fragment, before any session decision. */
  | { status: 'share' }
  /**
   * A guest grant's read-only view of one company (GRO-83). `unavailable`: the last
   * re-read of the session failed, so the view offers Retry in place of the reporting
   * screens. `reloadKey` is the auth generation after the last re-read; the reporting
   * screens fetch again when it moves.
   */
  | { status: 'guest'; guest: GuestSession; unavailable: boolean; reloadKey: number }
  /** The grant this browser held no longer works: revoked or expired. */
  | { status: 'guestEnded' }

/** One reconciled inventory: the session joined with the companies. */
export interface Inventory {
  user: SessionUser
  memberships: Membership[]
  clients: Client[]
}

/**
 * Why a caller asked for a reconciliation, held by that caller until its own read
 * settles. A routine reconciliation carries none, so it cannot erase a pending one.
 */
export type Intent =
  | { kind: 'accepted'; companyId: string; origin: 'shell' | 'entry' }
  | { kind: 'lost'; companyId: string; activeAtStart: string }
  | { kind: 'left'; companyId: string }
  /** The user deleted the company from the Members page (GRO-131, D6). */
  | { kind: 'deleted'; companyId: string }
  | { kind: 'created'; companyId: string }

/**
 * What a read said about an intent's company. `applied`: an accepted or created company
 * is in the joined list, or a lost, left or deleted one is gone from it. `kept`: such a
 * company is still there. `missing`: an accepted or created company is not there yet.
 */
export type IntentOutcome = 'applied' | 'kept' | 'missing'

export type EntryDecision =
  | 'invite' | 'guest' | 'guestEnded' | 'unauthenticated' | 'unavailable' | 'admin' | 'authenticated' | 'noAccess'
  | 'mismatch'

/**
 * The boundary's entry order (GRO-106 design, "Boundary flow"), as one function:
 *
 * 1. A held invite token: the invite page, signed in or not.
 * 1a. A guest session: the guest view.
 * 2. Unauthenticated: the ended-guest page when this browser holds the guest marker,
 *    else the sign-in screen.
 * 3. `/admin`, before the companies are read (`clients` still `undefined`): the caller
 *    probes the admin API and comes back with the companies on a denial.
 * 4. A non-empty joined list: the shell.
 * 5. No membership: No client access, for every user. Entry never routes to first run.
 *
 * Memberships that join to nothing are a `mismatch`: the caller reads once more. `null`
 * clients is a failed companies read.
 */
export function decideEntry(input: {
  session: 'authenticated' | 'guest' | 'unauthenticated' | 'unavailable'
  tokenHeld: boolean
  path: string
  /** Whether this browser holds the guest marker. */
  guestMarker?: boolean
  memberships?: Membership[]
  clients?: Client[] | null
}): EntryDecision {
  if (input.session === 'unavailable') return 'unavailable'
  if (input.tokenHeld) return 'invite'
  if (input.session === 'guest') return 'guest'
  if (input.session === 'unauthenticated') return input.guestMarker ? 'guestEnded' : 'unauthenticated'
  if (input.clients === undefined && isAdminPath(input.path)) return 'admin'
  if (input.clients === undefined || input.clients === null) return 'unavailable'
  if (input.clients.length > 0) return 'authenticated'
  if ((input.memberships ?? []).length === 0) return 'noAccess'
  return 'mismatch'
}

export interface Transition {
  state: BoundaryState
  /** The company to make active and save, when the transition changes it. */
  active: string | null
  outcomes: IntentOutcome[]
}

function outcomeOf(intent: Intent, has: (id: string) => boolean): IntentOutcome {
  if (intent.kind === 'lost' || intent.kind === 'left' || intent.kind === 'deleted') return has(intent.companyId) ? 'kept' : 'applied'
  return has(intent.companyId) ? 'applied' : 'missing'
}

/**
 * Applies one successful read to the current state, together with the intents of every
 * caller that shares the read, as one update (plan, Task 6.2). `activeId` is the company
 * the shell shows now, or the stored one outside the shell. The state decides first:
 *
 * - `invite` stays and takes the new inventory; only an Accept from the page enters the shell.
 * - `wizard` stays, also on zero memberships; only its own `created` intent enters the shell.
 * - `noAccess` enters the shell on any non-empty list, routine or not: an accepted company
 *   first, else the stored one, else the first.
 * - `authenticated` takes the new list. An Accept from the Invitations page keeps the
 *   active company and adds the joined notice; an Accept from entry switches to it. When
 *   the active company is gone it switches to the first remaining one, with the lost
 *   notice unless the user left or deleted it; with none left, No client access, except
 *   that deleting their own last company sends a user who may create companies to first
 *   run (GRO-131, D6).
 * - Every other state is not driven by a reconciliation and stays as it is.
 */
export function transition(state: BoundaryState, read: Inventory, intents: Intent[], activeId: string | null): Transition {
  const { user, memberships, clients } = read
  const has = (id: string) => clients.some((c) => c.id === id)
  const outcomes = intents.map((intent) => outcomeOf(intent, has))
  const shell = (notice: Notice | null = null): BoundaryState => ({ status: 'authenticated', user, memberships, clients, notice })
  const same = (next: BoundaryState): Transition => ({ state: next, active: null, outcomes })

  switch (state.status) {
    case 'invite': {
      const entered = intents.find((i) => i.kind === 'accepted' && i.origin === 'entry' && has(i.companyId))
      if (entered) return { state: shell(), active: entered.companyId, outcomes }
      return same({ ...state, user, memberships, clients })
    }
    case 'wizard': {
      const created = intents.find((i) => i.kind === 'created' && has(i.companyId))
      if (created) return { state: shell(), active: created.companyId, outcomes }
      return same({ ...state, user, memberships })
    }
    case 'noAccess': {
      if (clients.length === 0) return same({ ...state, user, memberships })
      const accepted = intents.find((i) => i.kind === 'accepted' && has(i.companyId))
      const active = accepted?.companyId ?? (activeId !== null && has(activeId) ? activeId : clients[0].id)
      return { state: shell(), active, outcomes }
    }
    case 'authenticated': {
      const left = (id: string) => intents.some((i) => (i.kind === 'left' || i.kind === 'deleted') && i.companyId === id)
      if (clients.length === 0) {
        const deleted = intents.some((i) => i.kind === 'deleted' && i.companyId === activeId)
        if (deleted && user.canCreateCompanies) return same({ status: 'wizard', user, memberships })
        const ownLeave = activeId !== null && left(activeId)
        const lost = state.clients.find((c) => c.id === activeId)
        return same({ status: 'noAccess', user, memberships, removed: ownLeave ? null : lost?.name ?? '' })
      }
      let active = activeId
      let notice = state.notice
      for (const intent of intents) {
        if (intent.kind !== 'accepted' || !has(intent.companyId)) continue
        if (intent.origin === 'entry') {
          active = intent.companyId
        } else {
          const joined = clients.find((c) => c.id === intent.companyId)!
          notice = { kind: 'joined', companyId: joined.id, name: joined.name, role: joined.role }
        }
      }
      if (active === null || !has(active)) {
        const lost = state.clients.find((c) => c.id === active)
        const next = clients[0]
        notice = active !== null && left(active) ? null : { kind: 'lost', lostName: lost?.name ?? '', nextName: next.name }
        active = next.id
      }
      return { state: shell(notice), active: active === activeId ? null : active, outcomes }
    }
    default:
      return same(state)
  }
}

/** An unauthenticated read: the invite page stays, signed out; everything else signs in. */
export function afterUnauthenticated(state: BoundaryState): BoundaryState {
  if (state.status === 'invite') return { ...state, user: null, memberships: [], clients: [] }
  return { status: 'unauthenticated' }
}

/**
 * A failed read stays where it is (plan, Task 6.2). Memberships that still join to
 * nothing after the follow-up are the one exception: the shell and No client access
 * cannot show an inventory they do not have, so they offer Retry.
 */
export function afterFailure(state: BoundaryState, mismatch: boolean): BoundaryState {
  if (mismatch && (state.status === 'authenticated' || state.status === 'noAccess')) return { status: 'unavailable' }
  return state
}
